import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  Avatar,
  Tooltip,
} from '@mui/material';
import {
  Warning,
  Error,
  Schedule,
  Person,
  DirectionsCar,
  Assignment,
  Visibility,
  NotificationImportant,
  Block,
} from '@mui/icons-material';

interface SLAAlert {
  id: string;
  type: 'missed-upload' | 'response-delay' | 'assignment-delay' | 'verification-pending';
  item: string;
  responsible: string;
  status: 'breached' | 'at-risk';
  slaTarget: string;
  current: string;
  priority: 'critical' | 'warning';
  description: string;
}

export const SLAAlerts: React.FC = () => {
  const [selectedAlert, setSelectedAlert] = React.useState<string | null>(null);

  // Mock SLA alerts data
  const slaAlerts: SLAAlert[] = [
    {
      id: '1',
      type: 'missed-upload',
      item: 'BMW 5 Series #8745',
      responsible: 'James Wilson (Host)',
      status: 'breached',
      slaTarget: '72h',
      current: '96h',
      priority: 'critical',
      description: 'Missing required insurance documentation',
    },
    {
      id: '2',
      type: 'response-delay',
      item: 'Mercedes C300 #8747',
      responsible: 'Sarah Johnson (Liaison)',
      status: 'breached',
      slaTarget: '6h',
      current: '9h',
      priority: 'critical',
      description: 'Delayed response to booking request',
    },
    {
      id: '3',
      type: 'assignment-delay',
      item: 'Audi Q5 #8746',
      responsible: 'Admin Team',
      status: 'at-risk',
      slaTarget: '24h',
      current: '18h',
      priority: 'warning',
      description: 'Driver assignment pending',
    },
    {
      id: '4',
      type: 'verification-pending',
      item: 'Tesla Model Y #8749',
      responsible: 'Michael Brown (Host)',
      status: 'at-risk',
      slaTarget: '72h',
      current: '48h',
      priority: 'warning',
      description: 'Vehicle verification documentation pending',
    },
    {
      id: '5',
      type: 'missed-upload',
      item: 'Range Rover Sport #8748',
      responsible: 'David Clark (Liaison)',
      status: 'breached',
      slaTarget: '72h',
      current: '92h',
      priority: 'critical',
      description: 'Multiple required documents missing',
    },
  ];

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'missed-upload':
        return <Assignment />;
      case 'response-delay':
        return <Schedule />;
      case 'assignment-delay':
        return <Person />;
      case 'verification-pending':
        return <DirectionsCar />;
      default:
        return <Warning />;
    }
  };

  const getPriorityColor = (priority: string) => {
    return priority === 'critical' ? 'error' : 'warning';
  };

  const getStatusColor = (status: string) => {
    return status === 'breached' ? 'error' : 'warning';
  };

  const handleAction = (alertId: string, action: 'remind' | 'escalate' | 'suspend' | 'view') => {
    // Implement action logic here
  };

  const criticalCount = slaAlerts.filter(alert => alert.priority === 'critical').length;
  const warningCount = slaAlerts.filter(alert => alert.priority === 'warning').length;

  return (
    <Card>
      <CardContent sx={{ p: 0 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 3,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              SLA Monitoring
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track response times and document completion against SLA rules
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              icon={<Error />}
              label={`${criticalCount} Critical`}
              color="error"
              variant="outlined"
            />
            <Chip
              icon={<Warning />}
              label={`${warningCount} Warning`}
              color="warning"
              variant="outlined"
            />
          </Box>
        </Box>

        {/* Alerts Table */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 600 }}>Alert Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Responsible</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>SLA Target</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Current</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {slaAlerts.map((alert) => (
                <TableRow
                  key={alert.id}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                    backgroundColor: selectedAlert === alert.id ? 'action.selected' : 'inherit',
                  }}
                  onClick={() => setSelectedAlert(alert.id)}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          backgroundColor: `${getPriorityColor(alert.priority)}.main`,
                        }}
                      >
                        {getAlertIcon(alert.type)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
                          {alert.type.replace('-', ' ')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {alert.description}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {alert.item}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {alert.responsible}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Chip
                      label={alert.status}
                      size="small"
                      color={getStatusColor(alert.status) as any}
                      variant="outlined"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {alert.slaTarget}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Typography
                      variant="body2"
                      color={alert.status === 'breached' ? 'error' : 'warning'}
                      sx={{ fontWeight: 500 }}
                    >
                      {alert.current}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAction(alert.id, 'view');
                          }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Send Reminder">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAction(alert.id, 'remind');
                          }}
                        >
                          <NotificationImportant fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      {alert.status === 'breached' && (
                        <Tooltip title="Suspend Account">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAction(alert.id, 'suspend');
                            }}
                          >
                            <Block fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Footer */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {slaAlerts.length} of {slaAlerts.length} alerts
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="outlined">
              Export Report
            </Button>
            <Button size="small" variant="contained">
              Configure SLA Rules
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};